import React from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { LayoutDashboard, Calendar, Wrench, Package, MessageSquare, Mail, LogOut, MapPin, Image, Settings } from 'lucide-react';
import logoUrl from '../../assets/logo.png';

const NAV = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/bookings', label: 'Bookings', icon: Calendar },
  { to: '/admin/rentals', label: 'Equipment Rentals', icon: Wrench },
  { to: '/admin/rv-sites', label: 'RV Sites', icon: MapPin },
  { to: '/admin/equipment', label: 'Equipment', icon: Package },
  { to: '/admin/gallery', label: 'Gallery', icon: Image },
  { to: '/admin/contacts', label: 'Inquiries', icon: MessageSquare },
  { to: '/admin/subscribers', label: 'Subscribers', icon: Mail },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

export const AdminLayout = ({ children }) => {
  const { isAuthed, checking, logout } = useAdminAuth();
  const location = useLocation();

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050814] flex items-center justify-center text-[#F4F1EA]/60">
        Loading admin...
      </div>
    );
  }
  if (!isAuthed) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#050814] text-[#F4F1EA]">
      {/* Top bar */}
      <header className="border-b border-[#B8860B]/20 bg-[#0B132B]">
        <div className="px-4 md:px-8 py-3 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3 group">
            <img
              src={logoUrl}
              alt="Creek Bottom Ranch"
              className="h-12 w-auto drop-shadow-[0_0_18px_rgba(212,175,55,0.2)] group-hover:scale-[1.04] transition-transform"
            />
            <div className="hidden sm:block">
              <h1 className="font-display text-xl text-[#F4F1EA] leading-tight">Admin</h1>
              <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Creek Bottom</p>
            </div>
          </Link>
          <Button
            data-testid="admin-logout-btn"
            onClick={logout}
            variant="outline"
            className="border-[#B8860B]/40 text-[#D4AF37] hover:bg-[#B8860B] hover:text-[#050814]"
          >
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <aside className="md:w-60 md:min-h-[calc(100vh-73px)] border-b md:border-b-0 md:border-r border-[#B8860B]/20 bg-[#0B132B]">
          <nav className="p-3 flex md:flex-col gap-1 overflow-x-auto">
            {NAV.map((item) => {
              const Icon = item.icon;
              const active = item.end
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-[#B8860B]/15 text-[#E5C158] border-l-2 border-[#D4AF37]'
                      : 'text-[#F4F1EA]/70 hover:bg-[#050814] hover:text-[#D4AF37]'
                  }`}
                >
                  <Icon size={18} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
